import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { Container, Table, Button, Alert, Badge } from 'react-bootstrap';

const PickList = () => {
  const [pickList, setPickList] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [ws, setWs] = useState(null);
  const [modalImage, setModalImage] = useState(null);
  const [bulkPickingBoosters, setBulkPickingBoosters] = useState(false);

  const videoRef = useRef(null);
  const readerRef = useRef(null);

  const maxRetries = 3;
  const retryDelayBase = 2000;

  const isProduction = process.env.NODE_ENV === 'production';
  const wsBaseURL = isProduction
    ? 'wss://thornsdashboard.com/tariqebay/notification2'
    : 'ws://localhost:3003?tariqebay/notification2';

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const getPickBins = (item) => {
    const possibleBins =
      item.pick_bins ||
      item.pickBins ||
      item.bins ||
      item.inventory_bins ||
      item.inventoryBins ||
      [];

    if (Array.isArray(possibleBins) && possibleBins.length > 0) {
      return possibleBins
        .map((bin) => ({
          bin_id: bin.bin_id || bin.binId || bin.id,
          location: bin.location || bin.bin_location || bin.binLocation || bin.name,
          quantity:
            bin.pick_quantity ||
            bin.pickQty ||
            bin.quantity_to_pick ||
            bin.quantityToPick ||
            bin.quantity ||
            bin.qty ||
            0,
        }))
        .filter((bin) => bin.bin_id || bin.location);
    }

    if (item.pick_from_bins && Array.isArray(item.pick_from_bins)) {
      return item.pick_from_bins
        .map((bin) => ({
          bin_id: bin.bin_id || bin.id,
          location: bin.location || bin.bin_location || bin.name,
          quantity: bin.quantity || bin.qty || bin.pick_quantity || 0,
        }))
        .filter((bin) => bin.bin_id || bin.location);
    }

    if (item.bin_summary || item.pick_bin_summary) {
      return [
        {
          bin_id: null,
          location: item.bin_summary || item.pick_bin_summary,
          quantity: null,
        },
      ];
    }

    if (item.bin_id || item.bin_location) {
      return [
        {
          bin_id: item.bin_id || null,
          location: item.bin_location || item.bin_id,
          quantity: item.quantity_ordered || 0,
        },
      ];
    }

    return [];
  };

  const renderPickFrom = (item) => {
    const bins = getPickBins(item);

    if (!bins.length) {
      return <span className="text-muted">N/A</span>;
    }

    return (
      <div className="d-flex flex-column gap-1">
        {bins.map((bin, index) => {
          const label = bin.location || bin.bin_id || 'Unknown';
          const qty = Number(bin.quantity || 0);

          return (
            <div key={`${item.order_id}-${item.sku}-bin-${bin.bin_id || label}-${index}`}>
              <Badge bg="info" text="dark" className="me-1">
                Bin {label}
              </Badge>

              {qty > 0 && (
                <Badge bg="secondary">
                  Pick {qty}
                </Badge>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const fetchPickList = useCallback(async (retryCount = 0) => {
    try {
      const response = await axios.get('/api/orders/picklist', {
        headers: getAuthHeaders(),
      });

      console.log(
        `[${new Date().toISOString()}] Pick list response:`,
        JSON.stringify(response.data, null, 2)
      );

      const orders = response.data.orders || [];

      setPickList(orders);
      setError(orders.length === 0 ? 'No pick list items found. Try generating the pick list.' : '');
    } catch (err) {
      console.error(
        `[${new Date().toISOString()}] Error fetching pick list:`,
        err.message,
        err.stack
      );

      if (err.response?.status === 429 && retryCount < maxRetries) {
        const delay = retryDelayBase * Math.pow(2, retryCount);

        console.log(
          `[${new Date().toISOString()}] Rate limit hit, retrying in ${delay}ms ` +
            `(attempt ${retryCount + 1}/${maxRetries})`
        );

        setTimeout(() => fetchPickList(retryCount + 1), delay);
        return;
      }

      setError(
        `Failed to load pick list: ${err.response?.data?.error || err.message || 'Server error'}`
      );
    }
  }, []);

  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      setError('Authentication required for real-time updates. Please log in.');
      return null;
    }

    const wsUrl = `${wsBaseURL}?token=${encodeURIComponent(token)}`;

    console.log(`[${new Date().toISOString()}] WebSocket: Connecting to ${wsUrl}`);

    const websocket = new WebSocket(wsUrl);

    websocket.onopen = () => {
      console.log(`[${new Date().toISOString()}] WebSocket connected`);
    };

    websocket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);

        console.log(`[${new Date().toISOString()}] WebSocket message:`, message);

        if (message.type === 'pickListUpdate') {
          setPickList((prev) => {
            const updatedItem = message.pickListItem;

            if (!updatedItem) return prev;

            const exists = prev.some((item) => item.id === updatedItem.id);

            if (!exists) return prev;

            return prev.map((item) =>
              item.id === updatedItem.id ? { ...item, ...updatedItem } : item
            );
          });
        }

        if (message.type === 'pickListRefresh') {
          fetchPickList();
        }
      } catch (err) {
        console.error(`[${new Date().toISOString()}] WebSocket: Error parsing message:`, err.message);
      }
    };

    websocket.onclose = () => {
      console.log(`[${new Date().toISOString()}] WebSocket disconnected`);
    };

    websocket.onerror = (err) => {
      console.error(`[${new Date().toISOString()}] WebSocket error:`, err);
    };

    setWs(websocket);

    return websocket;
  }, [fetchPickList, wsBaseURL]);

  const generatePickList = useCallback(
    async (retryCount = 0) => {
      try {
        setError('');

        await axios.post(
          '/api/orders/picklist/generate',
          {},
          {
            headers: getAuthHeaders(),
          }
        );

        console.log(`[${new Date().toISOString()}] Pick list generation triggered`);

        await fetchPickList();
      } catch (err) {
        console.error(
          `[${new Date().toISOString()}] Error generating pick list:`,
          err.message,
          err.stack
        );

        if (err.response?.status === 429 && retryCount < maxRetries) {
          const delay = retryDelayBase * Math.pow(2, retryCount);

          console.log(
            `[${new Date().toISOString()}] Rate limit hit, retrying in ${delay}ms ` +
              `(attempt ${retryCount + 1}/${maxRetries})`
          );

          setTimeout(() => generatePickList(retryCount + 1), delay);
          return;
        }

        setError(
          `Failed to generate pick list: ${
            err.response?.data?.error || err.message || 'Server error'
          }`
        );
      }
    },
    [fetchPickList]
  );

  const handleScan = useCallback(
    async (err, result, retryCount = 0) => {
      if (!result) return;

      try {
        const scannedSku = result.getText();

        const order = pickList.find((item) => {
          return String(item.sku || '').trim() === String(scannedSku || '').trim();
        });

        if (!order) {
          setError(`Invalid SKU scanned: ${scannedSku}`);
          return;
        }

        await axios.post(
          '/api/orders/picklist/scan',
          {
            sku: scannedSku,
            order_id: order.order_id,
          },
          {
            headers: getAuthHeaders(),
          }
        );

        console.log(
          `[${new Date().toISOString()}] Scanned SKU ${scannedSku} for order ${order.order_id}`
        );

        await fetchPickList();
      } catch (scanErr) {
        console.error(
          `[${new Date().toISOString()}] Error processing scan:`,
          scanErr.message,
          scanErr.stack
        );

        if (scanErr.response?.status === 429 && retryCount < maxRetries) {
          const delay = retryDelayBase * Math.pow(2, retryCount);

          console.log(
            `[${new Date().toISOString()}] Rate limit hit, retrying in ${delay}ms ` +
              `(attempt ${retryCount + 1}/${maxRetries})`
          );

          setTimeout(() => handleScan(err, result, retryCount + 1), delay);
          return;
        }

        setError(
          `Failed to process scan: ${
            scanErr.response?.data?.error || scanErr.message || 'Server error'
          }`
        );
      }
    },
    [pickList, fetchPickList]
  );

  const completePickList = useCallback(
    async (order_id, retryCount = 0) => {
      try {
        await axios.post(
          '/api/orders/picklist/complete',
          { order_id },
          {
            headers: getAuthHeaders(),
          }
        );

        console.log(`[${new Date().toISOString()}] Completed pick list for order ${order_id}`);

        await fetchPickList();
      } catch (err) {
        console.error(
          `[${new Date().toISOString()}] Error completing pick list:`,
          err.message,
          err.stack
        );

        if (err.response?.status === 429 && retryCount < maxRetries) {
          const delay = retryDelayBase * Math.pow(2, retryCount);

          console.log(
            `[${new Date().toISOString()}] Rate limit hit, retrying in ${delay}ms ` +
              `(attempt ${retryCount + 1}/${maxRetries})`
          );

          setTimeout(() => completePickList(order_id, retryCount + 1), delay);
          return;
        }

        setError(
          `Failed to complete pick list: ${
            err.response?.data?.error || err.message || 'Server error'
          }`
        );
      }
    },
    [fetchPickList]
  );

  const manuallyPickList = useCallback(
    async (order_id, sku, retryCount = 0) => {
      try {
        await axios.post(
          '/api/orders/picklist/manual-pick',
          { order_id, sku },
          {
            headers: getAuthHeaders(),
          }
        );

        console.log(
          `[${new Date().toISOString()}] Manually picked SKU ${sku} for order ${order_id}`
        );

        await fetchPickList();
      } catch (err) {
        console.error(
          `[${new Date().toISOString()}] Error manually picking:`,
          err.message,
          err.stack
        );

        if (err.response?.status === 429 && retryCount < maxRetries) {
          const delay = retryDelayBase * Math.pow(2, retryCount);

          console.log(`[${new Date().toISOString()}] Rate limit hit, retrying in ${delay}ms`);

          setTimeout(() => manuallyPickList(order_id, sku, retryCount + 1), delay);
          return;
        }

        setError(`Failed to manually pick order: ${err.response?.data?.error || err.message}`);
      }
    },
    [fetchPickList]
  );

  const pickAllBoosterPacks = useCallback(
    async (retryCount = 0) => {
      try {
        setBulkPickingBoosters(true);
        setError('');

        const response = await axios.post(
          '/api/orders/picklist/pick-all-boosters',
          {},
          {
            headers: getAuthHeaders(),
          }
        );

        console.log(
          `[${new Date().toISOString()}] Pick all booster packs response:`,
          response.data
        );

        await fetchPickList();
      } catch (err) {
        console.error(
          `[${new Date().toISOString()}] Error picking all booster packs:`,
          err.message,
          err.stack
        );

        if (err.response?.status === 429 && retryCount < maxRetries) {
          const delay = retryDelayBase * Math.pow(2, retryCount);

          console.log(
            `[${new Date().toISOString()}] Rate limit hit, retrying in ${delay}ms ` +
              `(attempt ${retryCount + 1}/${maxRetries})`
          );

          setTimeout(() => pickAllBoosterPacks(retryCount + 1), delay);
          return;
        }

        setError(
          `Failed to pick all booster packs: ${
            err.response?.data?.error || err.message || 'Server error'
          }`
        );
      } finally {
        setBulkPickingBoosters(false);
      }
    },
    [fetchPickList]
  );

  useEffect(() => {
    fetchPickList();

    const websocket = connectWebSocket();

    return () => {
      if (websocket) websocket.close();
    };
  }, [fetchPickList, connectWebSocket]);

  useEffect(() => {
    if (!scanning) {
      if (readerRef.current) {
        readerRef.current.reset();
        readerRef.current = null;
      }

      return;
    }

    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;

    reader
      .decodeFromVideoDevice(null, videoRef.current, (result, err) => {
        if (result) {
          handleScan(null, result);
        }

        if (err && err.name !== 'NotFoundException') {
          setError('Error during scanning: ' + err.message);
        }
      })
      .catch((scanErr) => {
        setError('Failed to start scanner: ' + scanErr.message);
      });

    return () => {
      if (readerRef.current) {
        readerRef.current.reset();
        readerRef.current = null;
      }
    };
  }, [scanning, handleScan]);

  return (
    <Container className="mt-4">
      <h1>Virtual Pick List</h1>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      <Button variant="primary" className="mb-4 me-2" onClick={generatePickList}>
        Generate Pick List
      </Button>

      <Button
        variant="primary"
        className="mb-4 me-2"
        onClick={() => setScanning((current) => !current)}
      >
        {scanning ? 'Stop Scanning' : 'Start Scanning'}
      </Button>

      <Button
        variant="success"
        className="mb-4"
        onClick={pickAllBoosterPacks}
        disabled={bulkPickingBoosters || pickList.length === 0}
      >
        {bulkPickingBoosters ? 'Picking Booster Packs...' : 'Pick All Booster Packs'}
      </Button>

      {scanning && (
        <div className="mb-4">
          <video ref={videoRef} style={{ width: '100%', maxWidth: '300px' }} />
        </div>
      )}

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th style={{ width: '70px' }}>Image</th>
            <th>SKU</th>
            <th>Item Name</th>
            <th>Qty Ordered</th>
            <th>Qty Picked</th>
            <th>Pick From</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>

        <tbody>
          {pickList.length === 0 ? (
            <tr>
              <td colSpan="8" className="text-center">
                No pick list items found
              </td>
            </tr>
          ) : (
            pickList.map((item) => (
              <tr key={`${item.order_id}-${item.sku}`}>
                <td style={{ width: '70px', height: '70px' }}>
                  {item.image_url ? (
                    <img
                      src={item.image_url}
                      alt={item.item_name || 'Item image'}
                      style={{
                        width: '50px',
                        height: '50px',
                        objectFit: 'cover',
                        cursor: 'pointer',
                      }}
                      onClick={() => setModalImage(item.image_url)}
                      onError={(e) => {
                        e.currentTarget.src = 'https://placehold.co/50x50';
                      }}
                    />
                  ) : (
                    'No image'
                  )}
                </td>

                <td>{item.sku}</td>

                <td>{item.item_name || 'N/A'}</td>

                <td>{item.quantity_ordered}</td>

                <td>{item.quantity_picked}</td>

                <td>{renderPickFrom(item)}</td>

                <td>
                  <Badge
                    bg={
                      item.status === 'completed'
                        ? 'success'
                        : item.status === 'in_progress'
                          ? 'warning'
                          : 'secondary'
                    }
                  >
                    {item.status || 'pending'}
                  </Badge>
                </td>

                <td>
                  {item.status !== 'completed' &&
                    Number(item.quantity_picked || 0) < Number(item.quantity_ordered || 0) && (
                      <>
                        <Button
                          variant="warning"
                          size="sm"
                          className="me-1"
                          onClick={() => manuallyPickList(item.order_id, item.sku)}
                        >
                          Pick
                        </Button>

                        {item.status === 'in_progress' && (
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => completePickList(item.order_id)}
                          >
                            Complete
                          </Button>
                        )}
                      </>
                    )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {modalImage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setModalImage(null)}
        >
          <img
            src={modalImage}
            alt="Enlarged"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
              border: '4px solid white',
            }}
          />
        </div>
      )}
    </Container>
  );
};

export default PickList;